import React, { useState, useEffect } from 'react';
import { audiencesAPI } from '../api/audiences';
import './AudienceSelector.css';

function AudienceSelector({
  websiteId,
  selectedAudience,
  onAudienceChange,
  onAudiencesChange,
  storageTargets = [],
  onAudienceContentImported,
  showManagementControls = true,
  showLabel = true,
  managementLayout = 'default',
}) {
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAudienceName, setNewAudienceName] = useState('');
  const [newAudienceDesc, setNewAudienceDesc] = useState('');
  const [newAudienceDomain, setNewAudienceDomain] = useState('');
  const [importFromAudienceId, setImportFromAudienceId] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [editAudienceId, setEditAudienceId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [editImportHtmlFromId, setEditImportHtmlFromId] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingAudience, setDeletingAudience] = useState(false);

  useEffect(() => {
    if (websiteId) {
      loadAudiences();
    }
  }, [websiteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAudiences = async () => {
    if (!websiteId) return;
    try {
      setLoading(true);
      const data = await audiencesAPI.getAll(websiteId);
      setAudiences(data);
      if (data.length > 0 && !selectedAudience) {
        onAudienceChange(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load audiences:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewAudienceName('');
    setNewAudienceDesc('');
    setNewAudienceDomain('');
    setImportFromAudienceId('');
  };

  const openEditModalForAudience = (aud) => {
    if (!aud) return;
    setEditAudienceId(aud.id);
    setEditName(aud.name || '');
    setEditDesc(aud.description != null ? aud.description : '');
    setEditDomain(aud.storageTargetId ? String(aud.storageTargetId) : '');
    setEditImportHtmlFromId('');
    setShowEditModal(true);
  };

  const openEditModal = () => {
    const aud = audiences.find((a) => String(a.id) === String(selectedAudience));
    openEditModalForAudience(aud);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditAudienceId(null);
    setEditImportHtmlFromId('');
  };

  const handleCreateAudience = async (e) => {
    e.preventDefault();
    if (!websiteId) {
      alert('Website ID is required');
      return;
    }
    if (!newAudienceName.trim()) {
      alert('Please enter an audience name');
      return;
    }

    try {
      const importId = importFromAudienceId || null;
      const domainOpt =
        newAudienceDomain === '' ? undefined : newAudienceDomain;
      const newAudience = await audiencesAPI.create(
        websiteId,
        newAudienceName,
        newAudienceDesc,
        importId,
        domainOpt
      );
      const updatedAudiences = [...audiences, newAudience];
      setAudiences(updatedAudiences);
      resetCreateForm();
      setShowCreateModal(false);
      onAudienceChange(newAudience.id);
      if (onAudiencesChange) {
        onAudiencesChange(updatedAudiences);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveEditAudience = async (e) => {
    e.preventDefault();
    if (!websiteId || !editAudienceId) return;
    if (!editName.trim()) {
      alert('Please enter an audience name');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        name: editName.trim(),
        description: editDesc,
        storageTargetId: editDomain === '' ? null : editDomain,
      };
      if (editImportHtmlFromId.trim() !== '') {
        payload.importHtmlFromAudienceId = editImportHtmlFromId.trim();
      }
      const updated = await audiencesAPI.update(websiteId, editAudienceId, payload);
      const data = await audiencesAPI.getAll(websiteId);
      setAudiences(data);
      if (onAudiencesChange) {
        onAudiencesChange(data);
      }
      if (String(editAudienceId) === String(selectedAudience) && updated.contentImported) {
        onAudienceContentImported?.(editAudienceId);
      }
      closeEditModal();
    } catch (err) {
      alert(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAudienceSelect = (e) => {
    const audienceId = e.target.value;
    onAudienceChange(audienceId);
  };

  const handleDeleteAudienceById = async (audienceId) => {
    if (!websiteId || !audienceId) return;
    if (audiences.length <= 1) {
      alert('You must keep at least one audience.');
      return;
    }
    const aud = audiences.find((a) => String(a.id) === String(audienceId));
    if (!aud) return;
    const confirmed = window.confirm(
      `Delete audience "${aud.name}"? This cannot be undone and will remove its saved content.`
    );
    if (!confirmed) return;

    setDeletingAudience(true);
    try {
      await audiencesAPI.delete(websiteId, audienceId);
      const updatedAudiences = audiences.filter((a) => String(a.id) !== String(audienceId));
      setAudiences(updatedAudiences);
      if (String(selectedAudience) === String(audienceId) && updatedAudiences.length > 0) {
        onAudienceChange(updatedAudiences[0].id);
      } else if (updatedAudiences.length === 0) {
        onAudienceChange(null);
      }
      if (onAudiencesChange) {
        onAudiencesChange(updatedAudiences);
      }
      if (showEditModal && String(editAudienceId) === String(audienceId)) {
        closeEditModal();
      }
    } catch (err) {
      alert(err.message || 'Failed to delete audience');
    } finally {
      setDeletingAudience(false);
    }
  };

  const handleDeleteAudience = async () => {
    await handleDeleteAudienceById(selectedAudience);
  };

  const hasTargets = storageTargets.length > 0;

  if (loading) {
    return <div className="audience-selector">Loading audiences...</div>;
  }

  return (
    <div className="audience-selector">
      {managementLayout !== 'list' && (
        <>
          {showLabel && (
            <label htmlFor="audience-select" className="audience-label">
              View Audience:
            </label>
          )}
          <select
            id="audience-select"
            value={selectedAudience || ''}
            onChange={handleAudienceSelect}
            className="audience-select"
          >
            {audiences.map((audience) => (
              <option key={audience.id} value={audience.id}>
                {audience.name}
              </option>
            ))}
          </select>
        </>
      )}
      {showManagementControls && managementLayout !== 'list' && (
        <>
          <button
            type="button"
            onClick={openEditModal}
            className="edit-audience-btn"
            title="Edit audience"
            disabled={!selectedAudience || audiences.length === 0}
          >
            ✎
          </button>
          <button
            type="button"
            onClick={handleDeleteAudience}
            className="delete-audience-btn"
            title="Delete selected audience"
            disabled={!selectedAudience || audiences.length <= 1 || deletingAudience}
          >
            {deletingAudience ? '…' : '🗑'}
          </button>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="create-audience-btn"
            title="Create new audience"
          >
            +
          </button>
        </>
      )}

      {showManagementControls && managementLayout === 'list' && (
        <div className="audience-manager-list">
          {audiences.map((audience) => (
            <div
              key={audience.id}
              className={`audience-manager-row ${
                String(audience.id) === String(selectedAudience) ? 'selected' : ''
              }`}
            >
              <button
                type="button"
                className="audience-manager-name"
                onClick={() => onAudienceChange(audience.id)}
                title="Switch to this audience"
              >
                {audience.name}
              </button>
              <div className="audience-manager-actions">
                <button
                  type="button"
                  onClick={() => openEditModalForAudience(audience)}
                  className="edit-audience-btn"
                  title={`Edit ${audience.name}`}
                >
                  ✎
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteAudienceById(audience.id)}
                  className="delete-audience-btn"
                  title={`Delete ${audience.name}`}
                  disabled={audiences.length <= 1 || deletingAudience}
                >
                  {deletingAudience ? '…' : '🗑'}
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="create-audience-btn audience-manager-add"
            title="Create new audience"
          >
            + Audience
          </button>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => { setShowCreateModal(false); resetCreateForm(); }}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Audience</h3>
            <form onSubmit={handleCreateAudience}>
              <div className="form-group">
                <label htmlFor="audience-name">Name:</label>
                <input
                  id="audience-name"
                  type="text"
                  value={newAudienceName}
                  onChange={(e) => setNewAudienceName(e.target.value)}
                  placeholder="e.g., elderly, couple"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="audience-desc">Description (optional):</label>
                <input
                  id="audience-desc"
                  type="text"
                  value={newAudienceDesc}
                  onChange={(e) => setNewAudienceDesc(e.target.value)}
                  placeholder="Description"
                />
              </div>
              {hasTargets && (
                <div className="form-group">
                  <label htmlFor="create-audience-domain">Domain (publish target):</label>
                  <select
                    id="create-audience-domain"
                    value={newAudienceDomain}
                    onChange={(e) => setNewAudienceDomain(e.target.value)}
                    className="import-select"
                  >
                    <option value="">Same as site default</option>
                    {storageTargets.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {audiences.length > 0 && (
                <div className="form-group">
                  <label htmlFor="import-from-audience">Import HTML from existing audience (optional):</label>
                  <select
                    id="import-from-audience"
                    value={importFromAudienceId}
                    onChange={(e) => setImportFromAudienceId(e.target.value)}
                    className="import-select"
                  >
                    <option value="">None — start with empty content</option>
                    {audiences.map((audience) => (
                      <option key={audience.id} value={audience.id}>
                        {audience.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-actions">
                <button type="submit">Create</button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editAudienceId != null && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Audience</h3>
            <form onSubmit={handleSaveEditAudience}>
              <div className="form-group">
                <label htmlFor="edit-audience-name">Name:</label>
                <input
                  id="edit-audience-name"
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-audience-desc">Description:</label>
                <input
                  id="edit-audience-desc"
                  type="text"
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  placeholder="Description"
                />
              </div>
              {hasTargets && (
                <div className="form-group">
                  <label htmlFor="edit-audience-domain">Domain (publish target):</label>
                  <select
                    id="edit-audience-domain"
                    value={editDomain}
                    onChange={(e) => setEditDomain(e.target.value)}
                    className="import-select"
                  >
                    <option value="">Same as site default</option>
                    {storageTargets.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {audiences.filter((a) => String(a.id) !== String(editAudienceId)).length > 0 && (
                <div className="form-group">
                  <label htmlFor="edit-import-html">
                    Overwrite HTML from another audience (optional):
                  </label>
                  <select
                    id="edit-import-html"
                    value={editImportHtmlFromId}
                    onChange={(e) => setEditImportHtmlFromId(e.target.value)}
                    className="import-select"
                  >
                    <option value="">No — keep current editor content</option>
                    {audiences
                      .filter((a) => String(a.id) !== String(editAudienceId))
                      .map((audience) => (
                        <option key={audience.id} value={audience.id}>
                          {audience.name}
                        </option>
                      ))}
                  </select>
                  <p className="form-hint">
                    Copies saved HTML, CSS, and components from the chosen audience. Clears published blob links until you save again.
                  </p>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={closeEditModal} disabled={savingEdit}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AudienceSelector;
